import { Users, UserCheck, UserX, Sparkles, type LucideIcon } from "lucide-react";
import type { ContactStatusFilter, ContactTileCounts, ContactTileStatus } from "@/utils/contacts";

interface Tile {
  status: ContactTileStatus;
  label: string;
  shortLabel: string;
  icon: LucideIcon;
  tone: string;
}

const TILES: Tile[] = [
  {
    status: "all",
    label: "Customers with a mobile",
    shortLabel: "Customers",
    icon: Users,
    tone: "bg-status-info/10 text-status-info",
  },
  {
    status: "opted_in",
    label: "Subscribed",
    shortLabel: "Subscribed",
    icon: UserCheck,
    tone: "bg-status-success/10 text-status-success",
  },
  {
    status: "opted_out",
    label: "Opted out",
    shortLabel: "Opted out",
    icon: UserX,
    tone: "bg-status-danger/10 text-status-danger",
  },
  {
    status: "invitable",
    label: "Invitable now",
    shortLabel: "Invitable now",
    icon: Sparkles,
    tone: "bg-status-warning/10 text-status-warning",
  },
];

interface ContactTilesProps {
  counts: ContactTileCounts | undefined;
  isLoading: boolean;
  isError: boolean;
  status: ContactStatusFilter;
  onSelect: (status: ContactStatusFilter) => void;
}

/**
 * The four roster tiles (useContactTileCounts: each counts exactly the rows its
 * filter lists). Each tile is a pressable filter: tap the number, see the
 * people behind it.
 */
export function ContactTiles({ counts, isLoading, isError, status, onSelect }: ContactTilesProps) {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" role="group" aria-label="Filter contacts by text club status">
      {TILES.map((tile) => {
        const pressed = status === tile.status;
        const value = isLoading ? "…" : isError || !counts ? "—" : Number(counts[tile.status] ?? 0).toLocaleString();
        return (
          <button
            key={tile.status}
            type="button"
            aria-pressed={pressed}
            onClick={() => onSelect(tile.status)}
            className={`flex min-h-11 flex-col gap-1.5 rounded-radius-card border bg-card p-3 text-left shadow-sm transition-shadow duration-200 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:p-4 ${
              pressed ? "border-action-primary ring-1 ring-action-primary" : "border-border"
            }`}
          >
            <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${tile.tone}`}>
              <tile.icon className="h-4 w-4" aria-hidden="true" />
            </span>
            <span className="text-2xl font-bold tabular-nums leading-tight text-foreground">{value}</span>
            <span className="text-xs font-medium text-muted-foreground sm:text-sm">
              <span className="sm:hidden">{tile.shortLabel}</span>
              <span className="hidden sm:inline">{tile.label}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
